/**
 * MagazineSubscribers.js
 * Admin subscribers registry showing Active, Expiring, and Expired users.
 * Supports inline detail Eye icon toggle, type filters, date filters, and pagination.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import {
  Users, RefreshCw, Eye, EyeOff, Search, Calendar, ChevronLeft, ChevronRight, Inbox, Filter
} from 'lucide-react';

const MagazineSubscribers = ({ toast }) => {
  const [activeTab, setActiveTab] = useState('active'); // active, expiring, expired
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_magazine_subscribers');
      return saved ? Math.max(1, parseInt(saved, 10)) : 1;
    } catch (e) {
      return 1;
    }
  });

  const setPage = useCallback((valOrFn) => {
    setPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_magazine_subscribers', String(next));
      } catch (e) {}
      return next;
    });
  }, []);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    type_filter: 'ALL',
    start_date: '',
    end_date: ''
  });

  // Track expanded row IDs for Eye toggle details
  const [expandedIds, setExpandedIds] = useState(new Set());

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMagazineSubscribers({
        page,
        page_size: 25,
        status_tab: activeTab,
        ...filters
      });
      if (res.success && res.data) {
        setSubscribers(res.data.subscribers || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / 25)));
      } else {
        toast.error(res.error || 'Failed to fetch subscribers.');
      }
    } catch {
      toast.error('Failed to load subscribers.');
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, filters, toast]);

  useEffect(() => {
    setPage(1); // Reset page on tab or filter change
    setExpandedIds(new Set()); // Reset expanded rows
  }, [activeTab, filters]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers, page]);

  const toggleExpandRow = (id) => {
    const next = new Set(expandedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedIds(next);
  };

  const formatDate = (iso) => {
    if (!iso) return 'Lifetime (Comped)';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-5 text-left">
        <div>
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <Users size={18} className="text-[#2ab391]" />
            Magazine Subscribers Registry
          </h3>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Monitor readers database, print distributions, and active guest lists.</p>
        </div>

        {/* Sub-tabs selectors (Active / Expiring / Expired) */}
        <div className="flex bg-gray-100/80 p-0.5 rounded-xl self-start md:self-center font-bold text-xs">
          {[
            { key: 'active', label: 'Active Subscribers' },
            { key: 'expiring', label: 'Expiring (30d)' },
            { key: 'expired', label: 'Expired' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-white text-gray-800 shadow-xs'
                  : 'text-gray-450 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter panel */}
      <div className="bg-gray-50/50 border border-gray-150 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 items-center text-left">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search email, name..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-4 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
          />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        {/* Type selector */}
        <div className="relative">
          <select
            value={filters.type_filter}
            onChange={(e) => setFilters(f => ({ ...f, type_filter: e.target.value }))}
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-gray-700 focus:outline-none focus:border-[#2ab391] appearance-none cursor-pointer"
          >
            <option value="ALL">Subscription Type: All</option>
            <option value="SUBSCRIBER">Subscriber (Paid)</option>
            <option value="GUEST">Guest Pass (Comped)</option>
          </select>
          <Filter size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Date Filter */}
        <div className="relative sm:col-span-2 flex items-center gap-2">
          <DateRangePicker
            startDate={filters.start_date}
            endDate={filters.end_date}
            onChange={(start, end) => {
              setFilters(f => ({ ...f, start_date: start, end_date: end }));
            }}
            placeholder="Select Date Range"
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none"
          />
          {(filters.start_date || filters.end_date || filters.search || filters.type_filter !== 'ALL') && (
            <button
              onClick={() => setFilters({ search: '', type_filter: 'ALL', start_date: '', end_date: '' })}
              className="text-xs font-bold text-red-500 hover:text-red-600 pl-1 cursor-pointer shrink-0"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Subscribers Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
          <span className="text-sm font-semibold text-gray-500">Retrieving Subscriber Lists...</span>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 border border-dashed border-gray-200 rounded-2xl bg-white">
          <Inbox size={32} className="text-gray-300" />
          <div>
            <p className="text-sm font-bold text-gray-700">No subscribers found</p>
            <p className="text-xs text-gray-400 mt-1">Try resetting the search filters or active tabs.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-150 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-150">
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Subscriber Name</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Expiry Date</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-semibold text-sm text-gray-700">
                  {subscribers.map((sub) => {
                    const isExpanded = expandedIds.has(sub.id);
                    return (
                      <React.Fragment key={sub.id}>
                        <tr className="hover:bg-gray-50/30 transition-colors">
                          <td className="px-5 py-3.5">
                            <div>
                              <p className="text-gray-800 font-bold">{sub.name}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{sub.user_email}</p>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 font-medium">
                            {formatDate(sub.start_date)}
                          </td>
                          <td className="px-5 py-3.5">
                            {sub.end_date ? (
                              <span className={`font-medium ${activeTab === 'expiring' ? 'text-amber-600' : 'text-gray-600'}`}>
                                {formatDate(sub.end_date)}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-teal-50 text-[#2ab391] text-[10px] font-bold">Lifetime</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            {sub.subscription_type === 'GUEST' ? (
                              <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-bold">Guest Pass</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[10px] font-bold">Paid Sub</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={() => toggleExpandRow(sub.id)}
                              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer inline-flex items-center gap-1 text-xs"
                            >
                              {isExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                              <span>{isExpanded ? 'Hide' : 'Details'}</span>
                            </button>
                          </td>
                        </tr>

                        {/* Expanded details container */}
                        {isExpanded && (
                          <tr className="bg-gray-50/40">
                            <td colSpan={5} className="px-5 py-4 border-t border-gray-100">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-semibold text-gray-600">
                                <div>
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Contact Info</p>
                                  <p className="text-gray-800 font-bold mt-1">Email: {sub.user_email}</p>
                                  <p className="text-gray-800 mt-0.5">Phone: {sub.phone}</p>
                                </div>
                                <div className="sm:col-span-2">
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Courier Dispatch Address</p>
                                  <p className="text-gray-800 font-bold mt-1">{sub.delivery_address}</p>
                                  <p className="text-gray-800 mt-0.5">Pincode: {sub.pincode}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs font-bold text-gray-500">
              <span>Showing Page {page} of {totalPages} ({totalItems} items)</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fetchSubscribers()}
                  disabled={loading}
                  title="Reload current page"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-lg font-bold text-xs transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                  Reload
                </button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MagazineSubscribers;
